import React, { FC } from 'react';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import { ReactComponent as ErrorIcon } from '@blink/assets/src/icons/alertCircle.svg';
import { ReactComponent as AlertIcon } from '@blink/assets/src/icons/warningNotFilled.svg';
import { ReactComponent as SafeIcon } from '@blink/assets/src/icons/safe.svg';

import { useHealthStateStyle } from './HealthState.style';
import { RiskFactorTooltip } from 'src/components/RiskFactor/v2/RiskFactorTooltip';
import { RiskFactorBar } from './RiskFactorBar';

import { BorrowModalData } from '@/shared/validations/borrowModal.schema';
import { useGetRiskLevel } from '@/shared/hooks/useGetRiskLevel';
import { Agreement } from '@/shared/api/borrow';
import { Loader, Skeleton } from '@/shared/ui';
import { formatWithCommas } from '@/shared/utils/helpers';

type BadgeProps = {
    health: string | null;
    agreement: Agreement;
};

const Badge = ({ health, agreement }: BadgeProps) => {
    const classes = useHealthStateStyle();

    const { loading, isLow, isWarning, isHigh } = useGetRiskLevel({
        health,
        marginCallThreshold: agreement.marginCallThreshold,
        liquidationThreshold: agreement.liquidationThreshold,
    });

    const { formState } = useFormContext<BorrowModalData>();

    const { errors } = formState;

    if (loading) {
        return (
            <div className={classes.healthBadgeLoading}>
                <Loader className='mr-3' />
                <div>Waiting</div>
            </div>
        );
    }

    const isErrors = errors && Object.keys(errors).length;

    const badgeClass = cn(classes.healthBadge, {
        [classes.healthBadgeSuccess]: isLow,
        [classes.healthBadgeWarning]: isWarning || isErrors,
        [classes.healthBadgeError]: isHigh,
    });

    if (isErrors || isWarning) {
        return (
            <div className={badgeClass}>
                <AlertIcon />
                <div>Caution</div>
            </div>
        );
    }

    if (isLow) {
        return (
            <div className={badgeClass}>
                <SafeIcon />
                <div>Safe</div>
            </div>
        );
    }

    if (isHigh) {
        return (
            <div className={badgeClass}>
                <ErrorIcon />
                <div>Warning</div>
            </div>
        );
    }

    return (
        <div className={classes.healthBadgeLoading}>
            <Loader className='mr-3' />
            <div>Waiting</div>
        </div>
    );
};

type CollateralHealthCardProps = {
    agreement: Agreement;
    health: string | null;
};

export const HealthState: FC<CollateralHealthCardProps> = ({ health, agreement }) => {
    const classes = useHealthStateStyle();

    const { loading, isLow, isWarning, isHigh, formattedHealth } = useGetRiskLevel({
        health,
        marginCallThreshold: agreement.marginCallThreshold,
        liquidationThreshold: agreement.liquidationThreshold,
    });

    const formattedRiskFactor = formatWithCommas(formattedHealth) || 'NaN';

    return (
        <>
            <div className={classes.rightCardWrapper}>
                <div className={classes.healthFactor}>
                    <div className={classes.flexWrapper}>
                        <RiskFactorTooltip />
                        <div className={classes.secondary}>/</div>
                        {loading || formattedRiskFactor === 'NaN' ? (
                            <Skeleton className={classes.micro} />
                        ) : (
                            <div
                                className={cn({
                                    [classes.success]: isLow,
                                    [classes.warning]: isWarning,
                                    [classes.error]: isHigh,
                                })}
                            >
                                {formattedRiskFactor}
                            </div>
                        )}
                    </div>
                    <Badge health={health} agreement={agreement} />
                </div>
                <RiskFactorBar health={health} agreement={agreement} />
            </div>
        </>
    );
};
